/**
 * SlicePanel — control panel for auto-slice visualization.
 *
 * Shows individual slice visibility toggles and per-slice
 * field/colormap controls. Simpler than IsosurfacePanel since
 * slices don't have criterion metadata.
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { ManifestLayer } from "./manifestHelpers";
import type { UVFViewerHandle } from "./UVFViewer";

export interface SlicePanelProps {
  layer: ManifestLayer;
  visibility: Map<string, boolean>;
  onToggle: (modelId: string, visible: boolean) => void;
  onClose: () => void;
  viewerHandle: UVFViewerHandle | null;
  /** Notify parent when the field or colormap changes (for cross-case persistence). */
  onFieldApplied?: (fieldName: string | undefined, colormapName: string | undefined) => void;
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EyeOpen = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PANEL: CSSProperties = {
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 25,
  width: 240,
  maxHeight: "calc(100% - 16px)",
  overflowY: "auto",
  background: "var(--color-surface, rgba(17,17,19,0.92))",
  borderRadius: 10,
  border: "1px solid var(--gray-a4)",
  backdropFilter: "blur(12px)",
  display: "flex",
  flexDirection: "column",
  fontSize: 11,
  color: "var(--gray-12)",
};

const SECTION: CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid var(--gray-a3)",
};

const LABEL: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: "var(--gray-9)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 4,
};

const SELECT: CSSProperties = {
  width: "100%",
  padding: "4px 6px",
  border: "1px solid var(--gray-a6)",
  borderRadius: 4,
  background: "var(--color-surface, #1a1a2e)",
  color: "var(--gray-12)",
  fontSize: 11,
};

const NUM_INPUT: CSSProperties = {
  ...SELECT,
  width: 80,
  textAlign: "center",
};

export const SlicePanel = memo(function SlicePanel({
  layer,
  visibility,
  onToggle,
  onClose,
  viewerHandle,
  onFieldApplied,
}: SlicePanelProps) {
  const children = layer.children ?? [];
  const childModelIds = useMemo(() => children.map((c) => c.id), [children]);

  const [fieldNames, setFieldNames] = useState<string[]>([]);
  const [activeField, setActiveField] = useState<string | undefined>();
  const [colormaps, setColormaps] = useState<string[]>([]);
  const [activeColormap, setActiveColormap] = useState<string | undefined>();
  const [range, setRange] = useState<[number, number] | null>(null);
  const [localMin, setLocalMin] = useState("");
  const [localMax, setLocalMax] = useState("");
  const rangeInitRef = useRef(false);

  // Refs for re-applying field after viewer instance changes (entity switch)
  const activeFieldRef = useRef(activeField);
  activeFieldRef.current = activeField;
  const activeColormapRef = useRef(activeColormap);
  activeColormapRef.current = activeColormap;

  useEffect(() => {
    if (!viewerHandle || childModelIds.length === 0) return;

    let cancelled = false;

    const discover = (): boolean => {
      for (const mid of childModelIds) {
        const names = viewerHandle.getFieldNamesForModel(mid);
        if (names.length > 0) {
          setFieldNames(names);
          return true;
        }
      }
      return false;
    };

    const apply = () => {
      setColormaps(viewerHandle.getColormaps());
      const field = activeFieldRef.current;
      if (field) {
        viewerHandle.setFieldForModels(childModelIds, field);
        const cmap = activeColormapRef.current;
        if (cmap) viewerHandle.setColormapForModels(childModelIds, cmap);
        const autoRange = viewerHandle.getFieldRangeForModels(childModelIds);
        if (autoRange) setRange(autoRange);
      }
    };

    if (discover()) {
      apply();
    } else {
      let attempts = 0;
      const poll = () => {
        if (cancelled || attempts >= 12) return;
        attempts++;
        setTimeout(() => {
          if (cancelled) return;
          if (discover()) {
            apply();
          } else {
            poll();
          }
        }, 500);
      };
      poll();
    }

    return () => { cancelled = true; };
  }, [viewerHandle, childModelIds]);

  useEffect(() => {
    if (range) {
      setLocalMin(range[0].toPrecision(4));
      setLocalMax(range[1].toPrecision(4));
      rangeInitRef.current = true;
    }
  }, [range?.[0], range?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyField = useCallback(
    (fieldName: string | undefined) => {
      if (!viewerHandle) return;
      setActiveField(fieldName);
      viewerHandle.setFieldForModels(childModelIds, fieldName);
      if (fieldName) {
        const autoRange = viewerHandle.getFieldRangeForModels(childModelIds);
        setRange(autoRange);
      } else {
        setRange(null);
      }
      onFieldApplied?.(fieldName, activeColormapRef.current);
    },
    [viewerHandle, childModelIds, onFieldApplied],
  );

  const handleColormapChange = useCallback(
    (name: string) => {
      if (!viewerHandle) return;
      setActiveColormap(name);
      viewerHandle.setColormapForModels(childModelIds, name);
      onFieldApplied?.(activeFieldRef.current, name);
    },
    [viewerHandle, childModelIds, onFieldApplied],
  );

  const commitRange = useCallback(() => {
    if (!viewerHandle) return;
    const min = parseFloat(localMin);
    const max = parseFloat(localMax);
    if (!isNaN(min) && !isNaN(max) && min < max) {
      viewerHandle.setFieldRangeForModels(childModelIds, min, max);
      setRange([min, max]);
    }
  }, [viewerHandle, childModelIds, localMin, localMax]);

  return (
    <div style={PANEL} className="f3v-slice-panel">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid var(--gray-a4)",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 12 }}>{layer.label}</span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--gray-9)",
            cursor: "pointer",
            padding: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Slice list */}
      <div style={{ ...SECTION, maxHeight: 180, overflowY: "auto" }}>
        <div style={LABEL}>Slices</div>
        {children.length === 0 && (
          <div style={{ color: "var(--gray-8)", fontSize: 10, padding: "4px 0" }}>
            No slices
          </div>
        )}
        {children.map((child) => {
          const vis = visibility.get(child.id) ?? child.defaultVisible;
          return (
            <button
              key={child.id}
              onClick={() => onToggle(child.id, !vis)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                width: "100%",
                padding: "3px 4px",
                border: "none",
                background: "transparent",
                color: "var(--gray-12)",
                fontSize: 10,
                cursor: "pointer",
                opacity: vis ? 1 : 0.5,
                borderRadius: 3,
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "var(--gray-a3)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "transparent")
              }
            >
              <span style={{ color: vis ? "var(--accent-9)" : "var(--gray-8)", flexShrink: 0 }}>
                {vis ? <EyeOpen /> : <EyeClosed />}
              </span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {child.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Field selector */}
      <div style={SECTION}>
        <div style={LABEL}>Color by</div>
        <select
          value={activeField ?? ""}
          onChange={(e) => applyField(e.target.value || undefined)}
          style={SELECT}
        >
          <option value="">None</option>
          {fieldNames.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {/* Colormap + range (when field active) */}
      {activeField && (
        <>
          {colormaps.length > 0 && (
            <div style={SECTION}>
              <div style={LABEL}>Colormap</div>
              <select
                value={activeColormap ?? ""}
                onChange={(e) => handleColormapChange(e.target.value)}
                style={SELECT}
              >
                {colormaps.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ ...SECTION, borderBottom: "none" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                <span style={{ fontSize: 10, color: "var(--gray-9)" }}>Min</span>
                <input
                  type="number"
                  value={localMin}
                  onChange={(e) => setLocalMin(e.target.value)}
                  onBlur={commitRange}
                  onKeyDown={(e) => e.key === "Enter" && commitRange()}
                  style={NUM_INPUT}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                <span style={{ fontSize: 10, color: "var(--gray-9)" }}>Max</span>
                <input
                  type="number"
                  value={localMax}
                  onChange={(e) => setLocalMax(e.target.value)}
                  onBlur={commitRange}
                  onKeyDown={(e) => e.key === "Enter" && commitRange()}
                  style={NUM_INPUT}
                />
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
});
